import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  arrayUnion,
  arrayRemove
} from "firebase/firestore";
import { Carrera, ResultadoCarrera } from "../models/types";
import { SocioServiceFirestore } from "./SocioServiceFirestore";
import { LigaServiceFirestore } from "./LigaServiceFirestore";

const carrerasCollection = collection(db, "carreras");

export const CarreraServiceFirestore = {
  // Toggle el estado de publicación de una carrera
  async togglePublicada(id: string, publicada: boolean) {
    try {
      console.log(`DEBUG - CarreraServiceFirestore.togglePublicada - Cambiando estado de publicación de carrera ${id} a ${publicada}`);
      const carreraRef = doc(db, "carreras", id);
      await updateDoc(carreraRef, { publicada });
      return true;
    } catch (error) {
      console.error('DEBUG - CarreraServiceFirestore.togglePublicada - Error:', error);
      throw error;
    }
  },
  async create(carrera: Omit<Carrera, "id">) {
    console.log('DEBUG - CarreraServiceFirestore.create - Creando carrera con datos:', carrera);
    
    // Si la carrera ya viene con resultados, usarlos directamente
    let carreraData = { ...carrera };
    
    // Si no tiene resultados, generarlos automáticamente
    if (!carreraData.resultados || carreraData.resultados.length === 0) {
      console.log('DEBUG - CarreraServiceFirestore.create - No hay resultados, generando automáticamente');
      // 1. Obtener todos los socios de la liga
      const socios = await SocioServiceFirestore.getAll();
      // Obtener la liga para saber su nombre
      const ligaDoc = doc(db, "ligas", carrera.ligaId || '');
      const ligaSnap = await getDoc(ligaDoc);
      const ligaNombre = ligaSnap.exists() ? ligaSnap.data().nombre : '';
      
      // Filtrar socios por Categoria en lugar de ligaAsignada
      const sociosLiga = socios.filter(s => (s.Categoria || '').trim().toUpperCase() === ligaNombre.trim().toUpperCase());
      console.log(`DEBUG - CarreraServiceFirestore.create - Encontrados ${sociosLiga.length} socios para la liga ${ligaNombre}`);

      // 2. Generar array de resultados para los socios de la liga
      // Filtrar socios que tienen ID definido
      const sociosConId = sociosLiga.filter(socio => typeof socio.id === 'string' && socio.id !== '');
      
      // Crear resultados solo con socios que tienen ID válido
      const resultados: ResultadoCarrera[] = sociosConId.map((socio, idx) => ({
          id: `resultado_${socio.id}_${Date.now()}`, // Generamos un ID único
          socioId: socio.id as string, // Aseguramos que es string con el filtro previo
          carreraId: '', // Se asignará después de crear la carrera
          posicion: idx + 1,
          puntos: 0,
          vueltaRapida: false,
          pole: false,
          polePosition: false, // Para compatibilidad con código existente
          dnf: false,
          noParticipacion: false,
          incidentes: 0,
          salidasPista: 0,
          sancionesAplicadas: [],
          puntosTotales: 0,
          puntosAdicionales: 0, // Para compatibilidad con código existente
          mejorConsistencia: false, // Para compatibilidad con código existente
          segundaMejorConsistencia: false // Para compatibilidad con código existente
      }));
      
      carreraData.resultados = resultados;
    } else {
      console.log(`DEBUG - CarreraServiceFirestore.create - Usando ${carreraData.resultados.length} resultados proporcionados`);
    }

    // 3. Crear la carrera en Firestore con todos los datos
    try {
      const docRef = await addDoc(carrerasCollection, carreraData);
      const carreraId = docRef.id;
      console.log('DEBUG - CarreraServiceFirestore.create - Carrera creada con ID:', carreraId);
      
      // 4. Actualizar la liga para añadir esta carrera a su array de carreras
      if (carreraData.ligaId) {
        console.log(`DEBUG - CarreraServiceFirestore.create - Actualizando liga ${carreraData.ligaId} para incluir la carrera ${carreraId}`);
        try {
          const ligaRef = doc(db, "ligas", carreraData.ligaId);
          await updateDoc(ligaRef, {
            carreras: arrayUnion(carreraId)
          });
          console.log(`DEBUG - CarreraServiceFirestore.create - Liga actualizada correctamente`);
        } catch (ligaError) {
          console.error('DEBUG - CarreraServiceFirestore.create - Error al actualizar la liga:', ligaError);
          // No lanzamos el error para que al menos la carrera se cree correctamente
        }
      }
      
      return { ...carreraData, id: carreraId };
    } catch (error) {
      console.error('DEBUG - CarreraServiceFirestore.create - Error al crear carrera:', error);
      throw error;
    }
  },

  async getAll(): Promise<Carrera[]> {
    const snapshot = await getDocs(carrerasCollection);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Carrera));
  },

  async getById(id: string): Promise<Carrera | null> {
    const docRef = doc(db, "carreras", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Carrera;
    }
    return null;
  },

  async update(id: string, carreraData: Partial<Carrera>) {
    const docRef = doc(db, "carreras", id);
    
    // Obtener la carrera actual para verificar si ha cambiado de liga
    const carreraActual = await this.getById(id);
    
    // Actualizar la carrera
    await updateDoc(docRef, carreraData);
    
    // Si ha cambiado la liga, actualizar las referencias en ambas ligas
    if (carreraData.ligaId && carreraActual && carreraData.ligaId !== carreraActual.ligaId) {
      console.log(`DEBUG - CarreraServiceFirestore.update - La carrera ${id} ha cambiado de liga ${carreraActual.ligaId} a ${carreraData.ligaId}`);
      
      // Eliminar la carrera de la liga anterior
      if (carreraActual.ligaId) {
        try {
          const ligaAnteriorRef = doc(db, "ligas", carreraActual.ligaId);
          await updateDoc(ligaAnteriorRef, {
            carreras: arrayRemove(id)
          });
          console.log(`DEBUG - CarreraServiceFirestore.update - Carrera eliminada de la liga anterior ${carreraActual.ligaId}`);
        } catch (error) {
          console.error(`DEBUG - CarreraServiceFirestore.update - Error al eliminar la carrera de la liga anterior:`, error);
        }
      }
      
      // Añadir la carrera a la nueva liga
      try {
        const nuevaLigaRef = doc(db, "ligas", carreraData.ligaId);
        await updateDoc(nuevaLigaRef, {
          carreras: arrayUnion(id)
        });
        console.log(`DEBUG - CarreraServiceFirestore.update - Carrera añadida a la nueva liga ${carreraData.ligaId}`);
      } catch (error) {
        console.error(`DEBUG - CarreraServiceFirestore.update - Error al añadir la carrera a la nueva liga:`, error);
      }
    }
  },

  async delete(id: string) {
    // Primero, obtener la carrera para saber a qué liga pertenece
    const carrera = await this.getById(id);
    
    // Eliminar la carrera
    const docRef = doc(db, "carreras", id);
    await deleteDoc(docRef);
    
    // Si la carrera pertenecía a una liga, actualizar la liga
    if (carrera && carrera.ligaId) {
      try {
        console.log(`DEBUG - CarreraServiceFirestore.delete - Eliminando carrera ${id} de la liga ${carrera.ligaId}`);
        const ligaRef = doc(db, "ligas", carrera.ligaId);
        await updateDoc(ligaRef, {
          carreras: arrayRemove(id)
        });
        console.log(`DEBUG - CarreraServiceFirestore.delete - Carrera eliminada de la liga correctamente`);
      } catch (error) {
        console.error(`DEBUG - CarreraServiceFirestore.delete - Error al eliminar la carrera de la liga:`, error);
      }
    }
  }
};
